import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsuarioLogado } from 'src/app/shared/interfaces/usuario/usuario-logado.interface';
import { UsuarioService } from 'src/app/shared/services/usuario.service';
import { WebSocketService } from 'src/app/shared/services/websocket.service';
import * as bootstrap from 'bootstrap';
import { environment } from 'src/environments/environment';
import { Notificacao } from 'src/app/shared/interfaces/notificacao.interface';
import { ChamadoAPIService } from 'src/app/modules/chamado/services/chamado-api.service';
import { PwaManagerService } from 'src/app/shared/services/pwa-manager.service';
import { PendenciaAvisoService } from 'src/app/shared/services/pendencia-aviso.service';
import { PendenciaAPIService } from 'src/app/modules/pendencias/services/pendencia-api.service';
import { SessaoService } from 'src/app/shared/services/sessao.service';
import { Pendencia } from 'src/app/shared/interfaces/pendencia/pendencia.interface';
import { NotificacaoUsuario } from 'src/app/shared/interfaces/notificacao/notificacao-usuario.interface';
import { NotificacaoAPIService } from 'src/app/shared/services/notificacao-api.service';
import { ChamadoAvisoService } from 'src/app/shared/services/chamado-aviso.service';

/** Linha da lista do ícone de pendências: a frase sai pronta (sem método no *ngFor). */
interface NovidadePendencia {
  pendencia: Pendencia;
  frase: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    public usuarioLogado: UsuarioLogado;
    public urlImagem: string = environment.AnexoEndpoint;
    // Sino: notificações gravadas (por enquanto, os avisos de chamado). O número é o de não lidas,
    // do servidor; a lista é carregada ao abrir o menu.
    public notificacoes: NotificacaoUsuario[] = [];
    public totalNotificacoes = 0;
    public carregandoNotificacoes = false;
    /** Só o modal legado #modal-nova-notificacao usa (nunca é aberto). */
    public notificacao: Notificacao;

    // Ícone de pendências: o contador é do servidor (novidade não vista); a lista é carregada ao
    // abrir o menu.
    public totalPendencias = 0;
    public novidadesPendencias: NovidadePendencia[] = [];
    public carregandoNovidadesPendencias = false;

    /** "Bom dia", "Boa tarde" ou "Boa noite", pelo horário de agora (no lugar do "Olá!" solto). */
    get saudacao(): string {
      const hora = new Date().getHours();
      if (hora < 12) return 'Bom dia';
      if (hora < 18) return 'Boa tarde';
      return 'Boa noite';
    }

    constructor(private usuarioService: UsuarioService, private router: Router, private wsService: WebSocketService, private chamadoApiService: ChamadoAPIService, private pwaManager: PwaManagerService,
      private pendenciaAviso: PendenciaAvisoService, private pendenciaApi: PendenciaAPIService, private sessao: SessaoService,
      private notificacaoApi: NotificacaoAPIService, private chamadoAviso: ChamadoAvisoService) { }

    /** Totais do usuário logado, direto do servidor. Ver o comentário no tratamento do evento. */
    private recarregarTotaisChamados(): void {
      this.chamadoApiService.getMeusTotais().pipe(takeUntil(this.destroy$)).subscribe(
        (totais) => this.usuarioService.setTotalChamados(totais),
        () => { }
      );
    }

    ngOnInit() {
        this.usuarioService.getUsuarioLogado().pipe(takeUntil(this.destroy$)).subscribe((usuario: UsuarioLogado) => {
          this.usuarioLogado = usuario;
        });

        this.pendenciaAviso.iniciar();
        this.pendenciaAviso.getContador().pipe(takeUntil(this.destroy$)).subscribe(total => {
          this.totalPendencias = total;
        });

        // Avisos de chamado: o toast é do ChamadoAvisoService; aqui, só o número do sino.
        this.chamadoAviso.iniciar();
        this.recarregarContadorNotificacoes();
        this.chamadoAviso.getAtualizacoes().pipe(takeUntil(this.destroy$)).subscribe(() => this.recarregarContadorNotificacoes());
        this.chamadoAviso.getLeituras().pipe(takeUntil(this.destroy$)).subscribe(() => this.recarregarContadorNotificacoes());

        this.wsService.getNotificacoesModel().pipe(takeUntil(this.destroy$)).subscribe((notificacao) => {
          if(notificacao != null){
            // NOVO_CHAMADO não existe mais: o aviso de chamado chega dirigido (ReceiveChamado),
            // gravado no sino. Por aqui seguem só os sinais de "recarregue os totais".
            if(notificacao.tipo == "ATUALIZAR_TOTAIS_CHAMADOS"){
              // O evento é enviado a TODOS os clientes, e os totais que ele
              // carrega são do usuário que agiu — aplicá-los aqui colocava a
              // fila de outra pessoa no badge de quem recebe. Era a causa
              // direta do "7 no menu com a tela vazia".
              //
              // Aqui ele vale só como aviso de "algo mudou": cada cliente
              // recarrega os próprios totais.
              this.recarregarTotaisChamados();
              }else if(notificacao.tipo == "ATUALIZAR_TOTAIS_SGI_ENVIO"){
              if(notificacao.usuariosPermitidos?.includes(this.usuarioLogado?.id)){
                this.usuarioService.setTotalSgiEnvio(notificacao.dados?.total);
              }
            } else if(notificacao.tipo == "ATUALIZAR_TOTAIS_SGI_APROVACOES"){
              if(notificacao.usuariosPermitidos?.includes(this.usuarioLogado?.id)){
                this.usuarioService.setTotalSgiAprovacao(notificacao.dados?.total);
              }
            } else if(notificacao.tipo == "ATUALIZAR_TOTAIS_SACPM_MINHAS"){
              // Diferente do evento de chamados, aqui o total é calculado por destinatário
              // e a lista de permitidos tem um usuário só — o responsável pelo plano. Pode
              // aplicar direto, sem recarregar.
              if(notificacao.usuariosPermitidos?.includes(this.usuarioLogado?.id)){
                this.usuarioService.setTotalSacpmMinhas(notificacao.dados?.total);
              }
            } else if(notificacao.tipo == "ATUALIZAR_TOTAIS_SACPM_VALIDACAO"){
              // Fila única do SGI: o mesmo total vale para todos os permitidos.
              if(notificacao.usuariosPermitidos?.includes(this.usuarioLogado?.id)){
                this.usuarioService.setTotalSacpmValidacao(notificacao.dados?.total);
              }
            }
          }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Ao abrir o menu do ícone de pendências: as mais recentes com novidade não vista. */
    carregarNovidadesPendencias(): void {
      this.carregandoNovidadesPendencias = true;
      this.pendenciaApi.novidades().pipe(takeUntil(this.destroy$)).subscribe({
        next: (itens) => {
          this.novidadesPendencias = (itens ?? []).map(p => ({ pendencia: p, frase: this.fraseNovidade(p) }));
          this.carregandoNovidadesPendencias = false;
        },
        error: () => {
          this.novidadesPendencias = [];
          this.carregandoNovidadesPendencias = false;
        }
      });
    }

    abrirPendencia(item: NovidadePendencia): void {
      this.router.navigate(['/pendencias/minhas-pendencias/visualizar', item.pendencia.id]);
    }

    verTodasPendencias(): void {
      this.router.navigate(['/pendencias/minhas-pendencias']);
    }

    trackByNovidadePendencia(_index: number, item: NovidadePendencia): number {
      return item.pendencia.id;
    }

    private fraseNovidade(p: Pendencia): string {
      const autor = p.usuarioUltimaNovidadeNome || 'Sistema';
      switch (p.acaoUltimaNovidade) {
        case 'criada': return `Nova pendência de ${autor}`;
        case 'encaminhada': return `${autor} encaminhou`;
        case 'comentada': return `${autor} comentou`;
        case 'respondida': return `${autor} respondeu`;
        case 'recusada': return `${autor} recusou`;
        case 'concluida': return `${autor} concluiu`;
        case 'cancelada': return `${autor} cancelou`;
        case 'aceita': return `${autor} concluiu a pendência`;
        case 'devolvida': return `${autor} devolveu a pendência`;
        case 'visualizada': return `${autor} visualizou`;
        default: return 'Pendência atualizada';
      }
    }

    logout(){
      // Revoga a sessão na API (senão o cookie continuaria entrando sozinho) e limpa o navegador;
      // as outras abas vão para o login pelo evento do storage.
      this.sessao.encerrar();
      this.router.navigateByUrl('/autenticacao/login');
    }

    /** Número do sino: notificações não lidas, do servidor. */
    private recarregarContadorNotificacoes(): void {
      if (!localStorage.getItem('token')) return;
      this.notificacaoApi.contador().pipe(takeUntil(this.destroy$)).subscribe({
        next: (c) => this.totalNotificacoes = c?.naoLidas ?? 0,
        // Contador é acessório: sem ele o header continua funcionando.
        error: () => { }
      });
    }

    /** Ao abrir o sino: as mais recentes, lidas e não lidas. */
    carregarNotificacoes(): void {
      this.carregandoNotificacoes = true;
      this.notificacaoApi.listar().pipe(takeUntil(this.destroy$)).subscribe({
        next: (itens) => {
          this.notificacoes = itens ?? [];
          this.carregandoNotificacoes = false;
        },
        error: () => {
          this.notificacoes = [];
          this.carregandoNotificacoes = false;
        }
      });
    }

    /** "Limpar": marca todas como lidas (continuam na lista). */
    limpar(){
      this.notificacaoApi.marcarTodasLidas().pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.notificacoes = this.notificacoes.map(n => ({ ...n, lida: true }));
          this.totalNotificacoes = 0;
        },
        error: () => this.recarregarContadorNotificacoes()
      });
    }

    abrirNotificacao(notificacao: NotificacaoUsuario): void {
      if (!notificacao.lida) {
        notificacao.lida = true;
        this.totalNotificacoes = Math.max(0, this.totalNotificacoes - 1);
        this.notificacaoApi.marcarLida(notificacao.id).pipe(takeUntil(this.destroy$)).subscribe({
          error: () => this.recarregarContadorNotificacoes()
        });
      }

      if (notificacao.origemEntidade === 'chamado' && notificacao.origemId) {
        this.chamadoAviso.abrirChamado(notificacao.origemId);
      }
    }

    trackByNotificacao(_index: number, item: NotificacaoUsuario): number {
      return item.id;
    }

    openModalNovaNotificacao(){  
      const modalElement = document.getElementById('modal-nova-notificacao');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);       
        modal.show(); // Exibe o modal
      }
    }

}
